import { Link } from "react-router-dom";
import { MapPin, Clock, Users, Heart } from "lucide-react";
import { LOGOS, CATEGORY_IMAGES } from "@/lib/api";
import { useStoreInfo } from "@/context/StoreInfoContext";

export default function About() {
  const store = useStoreInfo();
  return (
    <div data-testid="about-page">
      <section className="hero-grid">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="grid items-center gap-10 lg:grid-cols-2">
            <div>
              <span className="chip bg-brand-navy/10 text-brand-navy">Our story</span>
              <h1 className="mt-3 font-display text-4xl font-bold text-brand-navy sm:text-5xl lg:text-6xl">
                A game store built for the community
              </h1>
              <p className="mt-5 text-lg leading-relaxed text-slate-600">
                Elevation Gaming Co. started as a folding-table demo night in a coffee shop.
                Today, we&apos;re a full-service game store with 200+ titles in the play library,
                weekly tournaments across every major TCG, and a paint studio for miniature
                hobbyists.
              </p>
              <p className="mt-4 text-slate-600">
                Whether you&apos;re chasing your first booster pack pull, tuning a competitive deck,
                or just looking for a table to try Catan for the first time — we saved you a seat.
              </p>
            </div>
            <div className="relative">
              <div className="absolute -inset-6 rounded-[2.5rem] bg-gradient-to-br from-brand-teal/25 to-cyan-400/15 blur-2xl" />
              <img
                src={LOGOS.shield}
                alt="Elevation Gaming Co. shield"
                className="relative mx-auto w-64"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid gap-6 md:grid-cols-3">
          <Card icon={<Users />} title="Community First">
            Free open play any day of the week. Beginners always welcome. Learn-to-play
            sessions every Saturday morning.
          </Card>
          <Card icon={<Heart />} title="Curated with Love">
            1,500+ hand-picked products from Pokémon and MTG to Warhammer paints and
            premium sleeves — every SKU lives in our shop, not a distant warehouse.
          </Card>
          <Card icon={<Clock />} title="Open 7 Days">
            11am–10pm every day. Late nights for tournaments and paint & sip Mondays.
          </Card>
        </div>
      </section>

      <section className="bg-white py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
            <img
              src={CATEGORY_IMAGES["board-games"]}
              alt="Community play"
              className="aspect-[4/3] w-full rounded-3xl object-cover"
            />
            <div>
              <span className="chip bg-brand-teal/15 text-brand-teal">Visit us</span>
              <h2 className="mt-3 font-display text-3xl font-bold text-brand-navy sm:text-4xl">
                We&apos;re right off the main strip
              </h2>
              <p className="mt-4 text-slate-600">
                Plenty of parking, coffee across the street, and 20 tables ready for whatever
                game you bring through the door.
              </p>
              <div className="mt-6 space-y-2 text-sm text-slate-700">
                {store.address_full && (
                  <p className="flex items-center gap-2" data-testid="about-address">
                    <MapPin size={16} className="text-brand-teal shrink-0" /> {store.address_full}
                  </p>
                )}
                {store.hours && (
                  <p className="flex items-center gap-2" data-testid="about-hours">
                    <Clock size={16} className="text-brand-teal shrink-0" /> {store.hours}
                  </p>
                )}
              </div>
              <Link
                to="/contact"
                className="btn-primary mt-8 inline-flex rounded-full px-6 py-3 text-sm font-bold uppercase tracking-wide"
              >
                Get in touch
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Card({ icon, title, children }) {
  return (
    <div className="card-elevated rounded-2xl p-6">
      <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-brand-teal/10 text-brand-teal">
        {icon}
      </div>
      <h3 className="mt-4 font-display text-xl font-bold text-brand-navy">{title}</h3>
      <p className="mt-2 text-sm text-slate-600">{children}</p>
    </div>
  );
}
